import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { flowRight, isEmpty, isNil, isUndefined } from 'lodash';
import { SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { BACKTICK_CHAR, BACKTICK_CODE, EQUAL_CHAR, EQUAL_CODE } from 'common/constants/general';
import { preferenceKeyForUser } from 'common/constants/userPreferences';
import { useStore } from 'common/store';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withCellEventListener from 'components/ScalarSpreadsheet/utilities/withCellEventListener';
import { useLayoutContext } from 'context/LayoutContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  VALUATION_EXPAND_GPC_EXTRA_INFO_KEY,
  VALUATION_GPC_TABLE_SLUG,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import {
  VALUATION_EXPAND_TC_EXTRA_INFO_KEY,
  VALUATION_TC_TABLE_SLUG,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/config/constants';
import { subscribePreferenceChanges } from 'services/storage/preferencesIndexedDB';
import { getColumnLetter } from 'utilities/alphabet-utilities';
import useValidateDateGridComponent from 'utilities/useValidateDateGridComponent';
import { CellProps } from './Cell.props';
import { HoverSection } from './Cell.styles';
import {
  CellAttributes,
  CollapsibleColumns,
  FeaturedSpreadsheetContextType,
  UserStore,
  ValidateDateGridComponentReturn,
} from './Cell.types';
import { getUserPreferenceValue, setUserPreferenceValue } from '../../utilities/userPreferences';

const EXPANDED_STATES = [USER_EXPAND, SYSTEM_EXPAND];

const Cell = (props: CellProps) => {
  const prevActiveCellRef = useRef<ICell | undefined>(undefined);

  const {
    attributesRenderer,
    cell,
    className,
    backgroundColor,
    col,
    inLedger,
    onContextMenu,
    onFocus,
    onMouseDown,
    onMouseOver,
    row,
    selected,
    style: datasheetStyle,
  } = props;

  const {
    childIndex,
    colSpan,
    isChildWithDivider,
    isParent,
    isParentWithDivider,
    needsSpacer,
    isVisibleColumn,
    parentColumn,
    rowSpan,
    shouldDisplayTooltip = false,
  } = cell;

  const {
    activeCell,
    collapsibleColumns = {},
    displayLegend = true,
    displayNumbersAndColumns,
    isColumnDragging,
    isReadOnly,
    rowGroups,
    setActiveCell,
    setCollapsibleColumns,
    setDisplayNumbersAndColumns,
    tableTerms,
  } = useContext(FeaturedSpreadsheetContext) as unknown as FeaturedSpreadsheetContextType;

  const { tableSlug = undefined } = tableTerms ?? {};
  const { isExportMode } = useLayoutContext();

  const attributes: CellAttributes = useMemo(
    () => (attributesRenderer ? attributesRenderer(cell, row, col) : {}),
    [attributesRenderer, cell, row, col]
  );

  const columnId = attributes['data-column-id'];
  const parentId = attributes['data-col-parent'];

  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key ?? event.code;
      // if the key is a  simulate a click on the document to get the expr evaluation to happen
      if (cell.exprBuilderEnabled && key === '=') {
        setTimeout(() => {
          const clickTarget = document.body;
          const clickEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          clickTarget.dispatchEvent(clickEvent);
        }, 25);
        return;
      }

      if (key) {
        // Do not attempt to set the active cell if the function is not available in the context.
        // The function is valid for the ScalarSpreadsheet cells only.
        const initiatingKey = event.ctrlKey ?? event.metaKey ?? event.altKey;
        // add support for using ctrl + = in Mac
        const backtickOrEqualHit = [BACKTICK_CHAR, BACKTICK_CODE, EQUAL_CHAR, EQUAL_CODE].includes(key);
        if (initiatingKey && backtickOrEqualHit && setActiveCell && cell.expr) {
          let tmpCell = {} as ICell;
          const isActiveCellEmpty = isEmpty(activeCell);

          if (isActiveCellEmpty) {
            tmpCell = { ...cell };
          }

          // This will fire the first time that the key combination is pressed,
          // because by default we don't have the numbers, column legends and formula bar visible
          if (isActiveCellEmpty && !displayNumbersAndColumns && setDisplayNumbersAndColumns) {
            setDisplayNumbersAndColumns(true);
          } else if (isActiveCellEmpty && displayNumbersAndColumns) {
            // This will fire the second time that the key combination is pressed
            // At this point we should see the numbers and column legends
            setActiveCell(tmpCell);
          } else if (!isActiveCellEmpty && displayNumbersAndColumns && setDisplayNumbersAndColumns) {
            // This will fire the third time that the key combination is pressed
            // This time the numbers, column legends and formula bar will be hidden again
            setDisplayNumbersAndColumns(false);
            setActiveCell(undefined);
          }
        }
      }
    },
    [activeCell, cell, setActiveCell, displayNumbersAndColumns, setDisplayNumbersAndColumns]
  );

  const getClasses = useCallback(() => {
    let isVisible = false;
    if (!isNil(isVisibleColumn)) {
      isVisible = isVisibleColumn;
    } else {
      isVisible = isParent || isUndefined(collapsibleColumns) || !collapsibleColumns[parentColumn as string];
    }
    const hideLegend = !displayLegend && cell.isLegend;
    // if the isReadOnly variable is true, then add the 'read-only' string to the className variable if it isn't already there
    let cellClassName = className ?? '';
    if (isReadOnly && !className?.includes('read-only')) {
      cellClassName += ' read-only';
    }

    const classes = [
      cellClassName,
      isVisible && !hideLegend ? 'visible' : 'hidden',
      isChildWithDivider && isVisible ? 'child-with-divider' : '',
      isParentWithDivider ? 'parent-with-divider' : '',
      needsSpacer && isVisible ? 'projection-to-ltm-divider' : '',
    ];

    return classes.join(' ');
  }, [
    isReadOnly,
    cell.isLegend,
    className,
    collapsibleColumns,
    displayLegend,
    isChildWithDivider,
    isParent,
    isParentWithDivider,
    needsSpacer,
    isVisibleColumn,
    parentColumn,
  ]);

  const classes = useMemo(() => getClasses(), [getClasses]);

  useEffect(() => {
    if (selected && !inLedger) {
      document.addEventListener('keydown', keyUpHandler);
    }

    return () => document.removeEventListener('keydown', keyUpHandler);
  }, [selected, inLedger, keyUpHandler]);

  useEffect(() => {
    prevActiveCellRef.current = activeCell;
  }, [activeCell]);

  useEffect(() => {
    if (isColumnDragging) {
      return;
    }
    const prevActiveCell = prevActiveCellRef.current;
    if (selected && !isEmpty(prevActiveCell) && prevActiveCell?.key !== cell.key) {
      // Attempting to set the active cell breaks if there is more than one active cell
      if (setActiveCell) {
        setActiveCell({ ...cell, row, col } as ICell);
      }
    }
  }, [prevActiveCellRef, cell, col, selected, row, setActiveCell, isColumnDragging]);

  const cellClassName = useMemo(
    () => [!isEmpty(collapsibleColumns) && !collapsibleColumns[columnId as string] ? 'expanded' : ''].join(' '),
    [collapsibleColumns, columnId]
  );

  // we use this piece of state to determine of the cell was double clicked into or not.
  // child component (basically the ValueEditor, which is the only place this is relevant) is
  // responsible to unset this, otherwise it stays this way in perpetuity, which is fine
  // if it is not looking to make use of this feature.
  const [doubleClicked, setDoubleClicked] = useState<boolean>(false);

  // Create props with required structure for useValidateDateGridComponent
  const validationProps = {
    ...props,
    onDoubleClick: props.onDoubleClick || (() => {}), // Provide default if missing
  };

  const { doubleClickEvent, cellContent }: ValidateDateGridComponentReturn = useValidateDateGridComponent(
    validationProps as any,
    doubleClicked,
    setDoubleClicked
  );

  const onHover = useCallback(
    (hover: boolean) => {
      const cells = document.querySelectorAll(`td[data-column-id="${columnId}"] .left-border`);

      if (!isEmpty(collapsibleColumns) && cells) {
        cells.forEach(item => {
          if (hover) {
            item.classList.add('hovered');
          } else {
            item.classList.remove('hovered');
          }
        });
      }
    },
    [collapsibleColumns, columnId]
  );

  const endHover = useCallback(() => {
    onHover(false);
  }, [onHover]);

  const startHover = useCallback(() => {
    onHover(true);
  }, [onHover]);
  const dataCellKey = attributes.id ?? `CELL-${getColumnLetter(col)}${row}`;

  // GPC Extra Info
  const [{ user }] = useStore() as unknown as [UserStore];

  const isGPC = tableSlug === VALUATION_GPC_TABLE_SLUG;
  const isTC = tableSlug === VALUATION_TC_TABLE_SLUG;
  // undefined = still loading; null = loaded, no stored preference; boolean = loaded preference
  const [userPreferencesGPC, setUserPreferencesGPC] = useState<boolean | null | undefined>(undefined);
  const [userPreferencesTC, setUserPreferencesTC] = useState<boolean | null | undefined>(undefined);

  const needsGpcTcPreferences = isGPC || isTC;

  useEffect(() => {
    if (!needsGpcTcPreferences || !user?.id) {
      return;
    }
    getUserPreferenceValue<boolean>(user, VALUATION_EXPAND_GPC_EXTRA_INFO_KEY).then(value => {
      setUserPreferencesGPC(value ?? null);
    });
    getUserPreferenceValue<boolean>(user, VALUATION_EXPAND_TC_EXTRA_INFO_KEY).then(value => {
      setUserPreferencesTC(value ?? null);
    });
  }, [needsGpcTcPreferences, user]);

  // Cross-tab subscription only updates state — no direct onExpand() call here.
  // The mount effect below reacts to state and applies the preference exactly once.
  useEffect(() => {
    if (!needsGpcTcPreferences || !user?.id) {
      return undefined;
    }
    const gpcKey = preferenceKeyForUser(VALUATION_EXPAND_GPC_EXTRA_INFO_KEY, user.id as number);
    const tcKey = preferenceKeyForUser(VALUATION_EXPAND_TC_EXTRA_INFO_KEY, user.id as number);
    return subscribePreferenceChanges(({ key, value }) => {
      if (key === gpcKey) {
        setUserPreferencesGPC(value as boolean);
      }
      if (key === tcKey) {
        setUserPreferencesTC(value as boolean);
      }
    });
  }, [needsGpcTcPreferences, user]);

  const shouldExpandGPC = userPreferencesGPC !== false;
  const shouldExpandTC = userPreferencesTC !== false;

  const [hasBeenExpandedOnMount, setHasBeenExpandedOnMount] = useState<boolean>(false);

  const isCollapsableColumn = useMemo(() => !!(isParent || childIndex === 1), [childIndex, isParent]);
  const dataParentIsExpanded = useMemo(
    () => !isEmpty(collapsibleColumns) && isCollapsableColumn && !collapsibleColumns[columnId as string],
    [collapsibleColumns, columnId, isCollapsableColumn]
  );
  const shouldSavePreferencesGPC = useMemo(() => isCollapsableColumn && isGPC, [isCollapsableColumn, isGPC]);
  const shouldSavePreferencesTC = useMemo(() => isCollapsableColumn && isTC, [isCollapsableColumn, isTC]);

  const onExpand = useCallback(() => {
    const colId = isParent ? columnId : parentId;
    let isCollapsed = true;

    if (setCollapsibleColumns) {
      setCollapsibleColumns((prevCols: CollapsibleColumns) => {
        isCollapsed = !prevCols[colId as string];

        return {
          ...prevCols,
          [colId as string]: isCollapsed,
        };
      });
    }

    const childrenCells = document.querySelectorAll('td.child-with-divider');
    const parentCells = document.querySelectorAll('td.parent-with-divider');

    if (childrenCells?.length) {
      parentCells.forEach(parentCell => {
        parentCell.classList.add('hide');
      });
    } else {
      parentCells.forEach(parentCell => {
        parentCell.classList.remove('hide');
      });
    }

    return !isCollapsed;
  }, [columnId, isParent, parentId, setCollapsibleColumns]);

  // Save the Extra Info Tooltip preference
  const expandSavingPreferences = useCallback(() => {
    // First toggle the column state
    const newExpandedState = onExpand();

    // Save preferences without triggering storage events
    if (user && shouldSavePreferencesGPC) {
      setUserPreferenceValue(user, VALUATION_EXPAND_GPC_EXTRA_INFO_KEY, newExpandedState);
      setUserPreferencesGPC(newExpandedState);
    }
    if (user && shouldSavePreferencesTC) {
      setUserPreferenceValue(user, VALUATION_EXPAND_TC_EXTRA_INFO_KEY, newExpandedState);
      setUserPreferencesTC(newExpandedState);
    }
  }, [shouldSavePreferencesGPC, shouldSavePreferencesTC, user, onExpand]);

  // Apply preference once on mount (or after async load resolves) and on cross-tab changes.
  // Skips while preferences are still loading (undefined) to avoid the race where the
  // fallback-expand runs before the stored value arrives and permanently blocks it via the guard.
  useEffect(() => {
    if (!hasBeenExpandedOnMount && setCollapsibleColumns) {
      const colId = isParent ? columnId : parentId;
      let userPreference: boolean | null | undefined;

      if (isGPC && shouldSavePreferencesGPC) {
        userPreference = userPreferencesGPC;
      }
      if (isTC && shouldSavePreferencesTC) {
        userPreference = userPreferencesTC;
      }

      // Still loading — wait for the async read before applying anything.
      if (userPreference === undefined) {
        return;
      }

      // Stored preference: use it directly (false = expanded, true = collapsed in collapsibleColumns).
      if (userPreference !== null) {
        setCollapsibleColumns((prevCols: CollapsibleColumns) => ({
          ...prevCols,
          [colId as string]: !userPreference,
        }));
        setHasBeenExpandedOnMount(true);
        return;
      }

      // No stored preference — expand by default only when the tooltip is shown.
      if (shouldDisplayTooltip && (shouldExpandGPC || shouldExpandTC)) {
        setCollapsibleColumns((prevCols: CollapsibleColumns) => ({
          ...prevCols,
          [colId as string]: false, // false means expanded
        }));
        setHasBeenExpandedOnMount(true);
      }
    }
  }, [
    columnId,
    hasBeenExpandedOnMount,
    isGPC,
    isParent,
    isTC,
    parentId,
    setCollapsibleColumns,
    shouldDisplayTooltip,
    shouldExpandGPC,
    shouldExpandTC,
    shouldSavePreferencesGPC,
    shouldSavePreferencesTC,
    userPreferencesGPC,
    userPreferencesTC,
    user,
  ]);

  // Cross-tab changes after mount: apply directly without going through the mount-once effect.
  const applyCrossTabExpandPreference = useCallback(
    (shouldExpandExtraInfo: boolean | null, tableIsActive: boolean) => {
      if (shouldExpandExtraInfo === null) {
        return;
      }
      switch (shouldExpandExtraInfo) {
        case true:
          if (!dataParentIsExpanded && tableIsActive) {
            onExpand();
          }
          break;
        case false:
          if (dataParentIsExpanded && tableIsActive) {
            onExpand();
          }
          break;
        default:
          break;
      }
    },
    [dataParentIsExpanded, onExpand]
  );

  // Single cross-tab subscription: update state (so shouldExpandGPC/TC stays current) AND
  // apply the expand/collapse immediately after the initial mount guard has been satisfied.
  useEffect(() => {
    if (!needsGpcTcPreferences || !shouldDisplayTooltip || !user?.id) {
      return undefined;
    }
    return subscribePreferenceChanges(({ key, value }) => {
      const gpcKey = preferenceKeyForUser(VALUATION_EXPAND_GPC_EXTRA_INFO_KEY, user.id as number);
      const tcKey = preferenceKeyForUser(VALUATION_EXPAND_TC_EXTRA_INFO_KEY, user.id as number);
      if (key === gpcKey) {
        setUserPreferencesGPC(value as boolean);
        applyCrossTabExpandPreference(value as boolean, isGPC);
      }
      if (key === tcKey) {
        setUserPreferencesTC(value as boolean);
        applyCrossTabExpandPreference(value as boolean, isTC);
      }
    });
  }, [needsGpcTcPreferences, shouldDisplayTooltip, user?.id, isGPC, isTC, applyCrossTabExpandPreference]);

  const dataRowExpanded = useMemo(() => {
    const { alias = '', titleAlias = '', hasSubRows, showSubRows } = cell;
    if (!hasSubRows || !showSubRows || isEmpty(rowGroups)) {
      return false;
    }

    const rowState = rowGroups?.[alias] ?? rowGroups?.[titleAlias];

    return EXPANDED_STATES.includes(rowState);
  }, [cell, rowGroups]);

  const cellStyle = useMemo(() => {
    // Datasheet virtualization pins/widths arrive via style; keep them over cell.style.
    const merged = {
      ...(cell.style || {}),
      ...(backgroundColor ? { backgroundColor } : {}),
      ...(datasheetStyle || {}),
    };
    return Object.keys(merged).length ? merged : undefined;
  }, [cell.style, backgroundColor, datasheetStyle]);

  return (
    <td
      className={classes}
      colSpan={colSpan || undefined}
      data-cell-id={dataCellKey}
      data-parent-expanded={dataParentIsExpanded}
      data-row-expanded={dataRowExpanded}
      data-row-span={rowSpan}
      onContextMenu={onContextMenu}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      onMouseOver={onMouseOver}
      rowSpan={rowSpan || undefined}
      {...doubleClickEvent}
      {...attributes}
      // Apply after attributes so pin/width styles from the datasheet are not overwritten.
      style={cellStyle || undefined}>
      {!isExportMode && isCollapsableColumn && (
        <HoverSection
          className={cellClassName}
          onBlur={endHover}
          onClick={expandSavingPreferences}
          onFocus={startHover}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              expandSavingPreferences();
            }
          }}
          onMouseLeave={endHover}
          onMouseOver={startHover}>
          <Grid className="left-border" />
        </HoverSection>
      )}
      {cell.dragHandle}
      {cell.resizeHandle}
      {cellContent}
    </td>
  );
};

Cell.defaultProps = {
  selected: false,
  editing: false,
  updated: false,
  attributesRenderer: () => ({}),
};

const enhance = flowRight(withCellEventListener);
export default enhance(Cell);
